/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { MotionCard, MotionButton } from "../motion/AdminMotion";
import { adminGlass } from "../../styles/adminGlassTokens";


/* =========================
   PRESENTPLUS BRANDING
   Uses the school colour from AdminManager CSS vars.
   Falls back safely if the old shell has not been updated yet.
========================= */
const BRAND = "var(--pp-brand-primary, #4f46e5)";
const BRAND_DARK = "var(--pp-brand-dark, #3730a3)";
const BRAND_SOFT = "var(--pp-brand-soft, rgba(79,70,229,0.10))";
const BRAND_BORDER = "var(--pp-brand-border, rgba(79,70,229,0.22))";
const BRAND_SHADOW = "var(--pp-brand-shadow, rgba(79,70,229,0.24))";
const BRAND_GRADIENT = "linear-gradient(135deg, var(--pp-brand-primary, #2563eb), var(--pp-brand-dark, #4f46e5))";

function formatDate(value) {
  try {
    if (!value) return "—";
    if (value?.seconds) {
      return new Date(value.seconds * 1000).toLocaleString([], {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
}

export default function TeacherCsvImportDashboard({ schoolId }) {
  const [logs, setLogs] = useState([]);
  const [busyBatchId, setBusyBatchId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!schoolId) return;

    const ref = collection(db, "schools", schoolId, "auditLogs");

    const unsub = onSnapshot(
      query(ref),
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((item) => item.type === "teacher_csv_import")
          .sort((a, b) => {
            const aTime = a.createdAt?.seconds || 0;
            const bTime = b.createdAt?.seconds || 0;
            return bTime - aTime;
          });

        setLogs(rows);
      },
      (err) => {
        console.error("Teacher CSV import logs failed:", err);
        setError("Failed to load CSV import history.");
      }
    );

    return () => unsub();
  }, [schoolId]);

  const latestLogs = useMemo(() => logs.slice(0, 6), [logs]);

  async function undoImport(log) {
    if (!schoolId || !log?.importBatchId) return;

    const ok = window.confirm(
      "Undo this CSV import? Only pending invites from this batch will be revoked. Accepted staff accounts will not be deleted."
    );

    if (!ok) return;

    try {
      setBusyBatchId(log.importBatchId);
      setMessage("");
      setError("");

      const inviteIds = Array.isArray(log.inviteIds) ? log.inviteIds : [];
      let revokedCount = 0;
      let skippedCount = 0;

      for (const inviteId of inviteIds) {
        const inviteRef = doc(db, "schools", schoolId, "invites", inviteId);
        const inviteSnap = await getDoc(inviteRef);

        if (!inviteSnap.exists()) {
          skippedCount += 1;
          continue;
        }

        const invite = inviteSnap.data() || {};

        const isSafeToUndo =
          invite.importBatchId === log.importBatchId &&
          invite.source === "teacher_csv_import" &&
          String(invite.status || "").toLowerCase() === "pending";

        if (!isSafeToUndo) {
          skippedCount += 1;
          continue;
        }

        await updateDoc(inviteRef, {
          status: "revoked",
          revokedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        revokedCount += 1;
      }

      await updateDoc(doc(db, "schools", schoolId, "auditLogs", log.id), {
        status: "undone",
        undoneAt: serverTimestamp(),
        revokedCount,
        skippedCount,
        updatedAt: serverTimestamp(),
      });

      setMessage(
        `Import undone. ${revokedCount} pending invite${
          revokedCount === 1 ? "" : "s"
        } revoked.`
      );
    } catch (err) {
      console.error("Undo teacher CSV import failed:", err);
      setError(err?.message || "Failed to undo import.");
    } finally {
      setBusyBatchId("");
    }
  }

  return (
    <MotionCard style={styles.card} hover>
      <div style={styles.header}>
        <div>
          <div style={styles.eyebrow}>CSV audit</div>
          <h2 style={styles.title}>Teacher import history</h2>
          <p style={styles.text}>
            Review recent teacher CSV uploads and safely undo pending invites
            created by a batch.
          </p>
        </div>

        <div style={styles.totalPill}>{logs.length} imports</div>
      </div>

      {message ? <div style={styles.success}>{message}</div> : null}
      {error ? <div style={styles.error}>{error}</div> : null}

      {!latestLogs.length ? (
        <div style={styles.empty}>
          No teacher CSV imports yet. Once you import teachers, the batch history
          will appear here.
        </div>
      ) : (
        <div style={styles.list}>
          {latestLogs.map((log) => {
            const undone = log.status === "undone";
            const busy = busyBatchId === log.importBatchId;

            return (
              <div key={log.id} style={styles.row}>
                <div style={styles.main}>
                  <div style={styles.batchTitle}>
                    {log.importBatchId || log.id}
                  </div>

                  <div style={styles.meta}>
                    {formatDate(log.createdAt)} • Imported by{" "}
                    {log.createdByEmail || "admin"}
                  </div>
                </div>

                <div style={styles.stats}>
                  <span style={styles.statPill}>
                    {log.importedCount || 0} imported
                  </span>
                  <span style={styles.statPill}>
                    {Array.isArray(log.inviteIds) ? log.inviteIds.length : 0} invites
                  </span>
                  <span
                    style={{
                      ...styles.statusPill,
                      ...(undone ? styles.statusUndone : styles.statusDone),
                    }}
                  >
                    {undone ? "Undone" : "Completed"}
                  </span>
                </div>

                <div style={styles.actions}>
                  <MotionButton
                    type="button"
                    style={{
                      ...styles.undoButton,
                      ...(undone || busy ? styles.disabledButton : {}),
                    }}
                    disabled={undone || busy}
                    onClick={() => undoImport(log)}
                  >
                    {busy ? "Undoing..." : undone ? "Already undone" : "Undo"}
                  </MotionButton>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </MotionCard>
  );
}

const styles = {
  card: {
    ...adminGlass.panel,
    padding: 28,
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 18,
    marginBottom: 18,
  },

  eyebrow: {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    fontWeight: 900,
    color: "#64748b",
    marginBottom: 8,
  },

  title: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1.05,
    letterSpacing: "-0.04em",
    color: "#112042",
    fontWeight: 950,
  },

  text: {
    marginTop: 10,
    marginBottom: 0,
    color: "#64748b",
    fontSize: 15,
    lineHeight: 1.7,
    maxWidth: 760,
  },

  totalPill: {
    borderRadius: 999,
    padding: "10px 13px",
    background: BRAND_SOFT,
    color: BRAND_DARK,
    border: `1px solid ${BRAND_BORDER}`,
    fontSize: 12,
    fontWeight: 950,
    whiteSpace: "nowrap",
  },

  success: {
    padding: 14,
    borderRadius: 16,
    background: "#ecfdf3",
    border: "1px solid #86efac",
    color: "#166534",
    fontWeight: 900,
    marginBottom: 14,
  },

  error: {
    padding: 14,
    borderRadius: 16,
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#b91c1c",
    fontWeight: 900,
    marginBottom: 14,
  },

  empty: {
    padding: 18,
    borderRadius: 18,
    background: "rgba(255,255,255,0.58)",
    border: "1px solid rgba(219,231,255,0.86)",
    color: "#64748b",
    fontSize: 15,
    fontWeight: 800,
  },

  list: {
    display: "grid",
    gap: 12,
  },

  row: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.2fr) minmax(260px, auto) auto",
    gap: 14,
    alignItems: "center",
    padding: 16,
    borderRadius: 22,
    background: "rgba(255,255,255,0.58)",
    border: "1px solid rgba(219,231,255,0.86)",
    boxShadow: "0 10px 24px rgba(17,32,66,0.04)",
  },

  main: {
    minWidth: 0,
  },

  batchTitle: {
    color: "#112042",
    fontWeight: 950,
    fontSize: 15,
    wordBreak: "break-word",
    marginBottom: 5,
  },

  meta: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 800,
  },

  stats: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "flex-end",
  },

  statPill: {
    borderRadius: 999,
    padding: "8px 10px",
    background: "rgba(255,255,255,0.74)",
    color: "#475569",
    border: "1px solid #e2e8f0",
    fontSize: 12,
    fontWeight: 900,
  },

  statusPill: {
    borderRadius: 999,
    padding: "8px 10px",
    fontSize: 12,
    fontWeight: 950,
  },

  statusDone: {
    background: "#dcfce7",
    color: "#166534",
    border: "1px solid #bbf7d0",
  },

  statusUndone: {
    background: "#fff1f2",
    color: "#be123c",
    border: "1px solid #fecdd3",
  },

  actions: {
    display: "flex",
    justifyContent: "flex-end",
  },

  undoButton: {
    border: "none",
    borderRadius: 16,
    padding: "12px 15px",
    background: "#fff1f2",
    color: "#be123c",
    fontWeight: 950,
    cursor: "pointer",
    border: "1px solid #fecdd3",
  },

  disabledButton: {
    opacity: 0.55,
    cursor: "not-allowed",
  },
};